import React, {useContext, useState} from "react";
import {useTranslation} from "react-i18next";
import {AuthContext} from "../utils/AuthProvider";
import {FeedbackContext} from "../utils/FeedbackContext";
import {
    Container, Stack,
    Button,
    FormControl,
    FormHelperText,
    FormLabel,
    Input,
    Typography
} from "@mui/joy";
import {GlassCard} from "../components/GlassCard";
import {Layout} from "../components/Layout";
import {APIurl, stripeApiKey, stripeAppearance} from "../utils/Utils";
import firebase from "firebase/compat/app";
import {useNavigate} from "react-router-dom";
import {Elements, AddressElement} from "@stripe/react-stripe-js";
import type {StripeAddressElementChangeEvent} from "@stripe/stripe-js";

// Riscritto per usare Stripe <AddressElement> (gestisce da solo formati
// indirizzo/stato/CAP per qualunque paese, con lo stesso stile del
// pagamento) invece dei 7 campi custom di prima — che oltretutto
// venivano salvati in MySQL e mai più usati (non finivano sul Customer
// Stripe, quindi mai su una fattura vera). Ora l'indirizzo viene
// mandato a POST /billing/updateAddress, che lo attacca al Customer
// Stripe E lo salva in MySQL per precompilare il form la prossima volta.
export function BillingDetails() {
    const { t } = useTranslation();
    const {userData} = useContext(AuthContext)
    const {setError} = useContext(FeedbackContext)
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [taxId, setTaxId] = useState(userData?.billingTaxId ?? "");
    const [taxIdTouched, setTaxIdTouched] = useState(false);
    const [sdiCode, setSdiCode] = useState(userData?.billingSdiCode ?? "");
    const [addressEvent, setAddressEvent] = useState<StripeAddressElementChangeEvent | null>(null);

    const taxIdMissing = taxId.trim() === "";
    const canSubmit = addressEvent?.complete && !taxIdMissing && !loading;

    async function submit() {
        if (!addressEvent?.complete) return;
        if (taxIdMissing) {
            setTaxIdTouched(true);
            return;
        }
        setLoading(true);
        try {
            const user = firebase.auth().currentUser;
            if (!user) {
                throw new Error(t("usernotauthenticated"));
            }
            const token = await user.getIdToken();

            const response = await fetch(`${APIurl}/billing/updateAddress`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: addressEvent.value.name,
                    address: addressEvent.value.address,
                    taxId: taxId.trim(),
                    sdiCode: sdiCode.trim() || undefined,
                })
            })
            if (!response.ok) {
                throw new Error(t("genericerror"));
            }

            navigate("/pay");
        } catch (e: unknown) {
            setError(e);
        } finally {
            setLoading(false)
        }
    }

    return (
        <Layout>
            <Container maxWidth="sm" sx={{py: {xs: 3, sm: 5}, px: {xs: 2, sm: 3}}}>
                <Stack spacing={3} sx={{width: "100%"}}>
                    <Stack spacing={0.5}>
                        <Typography level="title-lg" fontWeight="bold">{t("billingdetails")}</Typography>
                        <Typography level="body-sm" sx={{color: "neutral.400"}}>{t("billingdetailssubtitle")}</Typography>
                    </Stack>
                    <GlassCard>
                        <Elements stripe={stripeApiKey} options={{appearance: stripeAppearance}}>
                            <AddressElement
                                options={{
                                    mode: "billing",
                                    display: {name: "full"},
                                    defaultValues: {
                                        name: userData?.billingName ?? undefined,
                                        address: userData?.billingAddress ? {
                                            line1: userData.billingAddress.line1,
                                            line2: userData.billingAddress.line2 ?? undefined,
                                            city: userData.billingAddress.city,
                                            state: userData.billingAddress.state ?? undefined,
                                            postal_code: userData.billingAddress.postal_code,
                                            country: userData.billingAddress.country,
                                        } : undefined,
                                    },
                                }}
                                onChange={(event) => setAddressEvent(event)}
                            />
                        </Elements>
                    </GlassCard>
                    <FormControl required error={taxIdTouched && taxIdMissing}>
                        <FormLabel>{t("taxidrequired")}</FormLabel>
                        <Input
                            type="text"
                            value={taxId}
                            onChange={e => setTaxId(e.target.value)}
                            onBlur={() => setTaxIdTouched(true)}
                            placeholder={t("taxidplaceholder")}
                        />
                        {taxIdTouched && taxIdMissing && (
                            <FormHelperText>{t("taxidrequirederror")}</FormHelperText>
                        )}
                    </FormControl>
                    <FormControl>
                        <FormLabel>{t("sdicodeoptional")}</FormLabel>
                        <Input
                            type="text"
                            value={sdiCode}
                            onChange={e => setSdiCode(e.target.value)}
                            placeholder={t("sdicodeplaceholder")}
                        />
                    </FormControl>
                    <Button
                        disabled={!canSubmit}
                        loading={loading}
                        onClick={submit}
                        size="lg"
                    >
                        {t("next")}
                    </Button>
                </Stack>
            </Container>
        </Layout>
    )
}
